import { BRIEF_WORDING_RULES, type BriefEdit, type BriefWordingRule } from '@sigora/domain';

import { domainCodeOf, isApiError } from '../../api/errors.ts';
import { BRIEF_EDIT_FAILED, SUMMARY_EMPTY_COPY, WORDING_REFUSED_COPY } from '../brief-copy.ts';

/**
 * The refusal that means the Brief on screen is no longer the version the server holds: another
 * edit, or a Sigora revision, landed first. The Brief is read again, the page says it changed, and
 * the edit is left open.
 */
export const BRIEF_MOVED_CODES: ReadonlySet<string> = new Set(['artifact_version_not_current']);

/**
 * The refusal that means the study holds no published Brief any more. The Brief is read again and
 * the read says so; nothing claims it changed under an edit.
 */
export const BRIEF_UNPUBLISHED_CODE = 'brief_not_published';

/**
 * How one framing edit ended: saved (the Brief on screen is the one the edit answered with), moved
 * (the Brief is being read again, and what was typed stays open), unpublished (the study has no
 * Brief to edit), or refused with the failure the block words for itself.
 */
export type BriefEditOutcome =
  | { status: 'saved' }
  | { status: 'moved' }
  | { status: 'unpublished' }
  | { status: 'refused'; failure: unknown };

/** Sends one framing edit over the version on screen; see `useBriefRead`. */
export type SendBriefEdit = (edit: BriefEdit) => Promise<BriefEditOutcome>;

const isWordingRule = (value: unknown): value is BriefWordingRule =>
  (BRIEF_WORDING_RULES as readonly unknown[]).includes(value);

/**
 * What a refused edit says under the block it was made on: the rule a Verdict's wording broke,
 * the last bullet showing, or that the change could not be saved. The server's own message is
 * never shown; it is prose for whoever reads the logs.
 */
export const briefEditRefusal = (failure: unknown): string => {
  const code = domainCodeOf(failure);
  if (
    code === 'brief_wording_refused' &&
    isApiError(failure) &&
    isWordingRule(failure.detail.rule)
  ) {
    return WORDING_REFUSED_COPY[failure.detail.rule];
  }
  if (code === 'brief_summary_empty') {
    return SUMMARY_EMPTY_COPY;
  }

  return BRIEF_EDIT_FAILED;
};
